import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Check, X, Search } from 'lucide-react'
import { apiClient } from '@api'
import './FeatureSelector.css'

/**
 * FeatureSelector Component
 *
 * Multi-select component for selecting package features in package forms
 *
 * Features:
 * - Multi-select with checkboxes
 * - Search/filter features
 * - Group by category
 * - Display selected count
 * - Visual feedback for selected items
 * - Loading and error states
 * - Scrollable list
 *
 * @param {Array} value - Array of selected feature IDs
 * @param {Function} onChange - Callback when selection changes
 * @param {Boolean} disabled - Disable the selector
 * @param {String} error - Error message to display
 */
const FeatureSelector = ({ value = [], onChange, disabled = false, error = null }) => {
	const [features, setFeatures] = useState([])
	const [loading, setLoading] = useState(true)
	const [searchTerm, setSearchTerm] = useState('')
	const [fetchError, setFetchError] = useState(null)

	// Fetch features from API
	useEffect(() => {
		const fetchFeatures = async () => {
			try {
				setLoading(true)
				setFetchError(null)

				const response = await apiClient.get('/features?status=active&limit=200')

				// Handle API response format: { status, message, data: [...], meta: {...} }
				// Backend returns { status, message, data: [...], meta: {...} }
				const featuresArray = response.data && Array.isArray(response.data) 
					? response.data 
					: []

				// Convert feature IDs to integers for consistent comparison
				const normalizedFeatures = featuresArray.map((feature) => ({
					...feature,
					id: parseInt(feature.id, 10),
				}))
				setFeatures(normalizedFeatures)
			} catch (err) {
				console.error('Failed to fetch features:', err)
				const errorMessage = err.response?.data?.message || err.message || 'Failed to load features'
				setFetchError(errorMessage)
				setFeatures([]) // Set empty array on error to prevent UI issues
			} finally {
				setLoading(false)
			}
		}

		fetchFeatures()
	}, [])

	// Filter features based on search term
	const filteredFeatures = features.filter((feature) =>
		feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
		(feature.category && feature.category.toLowerCase().includes(searchTerm.toLowerCase()))
	)

	// Group features by category
	const groupedFeatures = filteredFeatures.reduce((acc, feature) => {
		const category = feature.category || 'Uncategorized'
		if (!acc[category]) {
			acc[category] = []
		}
		acc[category].push(feature)
		return acc
	}, {})

	// Sort categories alphabetically
	const sortedCategories = Object.keys(groupedFeatures).sort()

	// Handle checkbox change
	const handleToggle = (featureId) => {
		if (disabled) return

		const newValue = value.includes(featureId)
			? value.filter((id) => id !== featureId) // Remove if already selected
			: [...value, featureId] // Add if not selected

		onChange(newValue)
	}

	// Handle select all in category
	const handleSelectAllCategory = (categoryFeatures) => {
		if (disabled) return

		const categoryIds = categoryFeatures.map((f) => f.id)
		const allSelected = categoryIds.every((id) => value.includes(id))

		if (allSelected) {
			// Deselect all in category
			onChange(value.filter((id) => !categoryIds.includes(id)))
		} else {
			// Select all in category
			const newValue = [...new Set([...value, ...categoryIds])]
			onChange(newValue)
		}
	}

	// Handle select all
	const handleSelectAll = () => {
		if (disabled) return

		if (value.length === filteredFeatures.length) {
			// Deselect all
			onChange([])
		} else {
			// Select all filtered features
			const allIds = filteredFeatures.map((feature) => feature.id)
			onChange(allIds)
		}
	}

	// Handle clear selection
	const handleClearAll = () => {
		if (disabled) return
		onChange([])
	}

	const selectedCount = value.length

	return (
		<div className="feature-selector">
			{/* Header with count */}
			<div className="feature-selector__header">
				<label className="feature-selector__label">
					Features
					{selectedCount > 0 && (
						<span className="feature-selector__count">{selectedCount} selected</span>
					)}
				</label>

				{selectedCount > 0 && (
					<button
						type="button"
						className="feature-selector__clear-btn"
						onClick={handleClearAll}
						disabled={disabled}
						title="Clear all selections"
					>
						<X size={14} />
						Clear
					</button>
				)}
			</div>

			{/* Search and Select All Row */}
			<div className="feature-selector__toolbar">
				<div className="feature-selector__search">
					<Search size={16} className="feature-selector__search-icon" />
					<input
						type="text"
						className="feature-selector__search-input"
						placeholder="Search features..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						disabled={disabled || loading}
					/>
					{searchTerm && (
						<button
							type="button"
							className="feature-selector__search-clear"
							onClick={() => setSearchTerm('')}
							disabled={disabled || loading}
							title="Clear search"
						>
							<X size={14} />
						</button>
					)}
				</div>

				{!loading && filteredFeatures.length > 0 && (
					<button
						type="button"
						className="feature-selector__select-all"
						onClick={handleSelectAll}
						disabled={disabled}
					>
						{value.length === filteredFeatures.length ? 'Deselect All' : 'Select All'}
					</button>
				)}
			</div>

			{/* Feature List */}
			<div className="feature-selector__list">
				{loading && (
					<div className="feature-selector__loading">
						<div className="spinner" />
						<span>Loading features...</span>
					</div>
				)}

				{fetchError && (
					<div className="feature-selector__error">
						<span>{fetchError}</span>
					</div>
				)}

				{!loading && !fetchError && sortedCategories.length === 0 && (
					<div className="feature-selector__empty">
						{searchTerm
							? `No features found for "${searchTerm}"`
							: 'No features available'}
					</div>
				)}

				{!loading &&
					!fetchError &&
					sortedCategories.map((category) => {
						const categoryFeatures = groupedFeatures[category]
						const categoryIds = categoryFeatures.map((f) => f.id)
						const allCategorySelected = categoryIds.every((id) => value.includes(id))
						const someCategorySelected = categoryIds.some((id) => value.includes(id))

						return (
							<div key={category} className="feature-selector__category">
								<div className="feature-selector__category-header">
									<label className="feature-selector__category-label">
										<input
											type="checkbox"
											className="feature-selector__category-checkbox"
											checked={allCategorySelected}
											ref={(input) => {
												if (input) input.indeterminate = someCategorySelected && !allCategorySelected
											}}
											onChange={() => handleSelectAllCategory(categoryFeatures)}
											disabled={disabled}
										/>
										<span className="feature-selector__category-name">{category}</span>
										<span className="feature-selector__category-count">
											({categoryFeatures.length})
										</span>
									</label>
								</div>

								<div className="feature-selector__category-items">
									{categoryFeatures.map((feature) => {
										const isSelected = value.includes(feature.id)

										return (
											<label
												key={feature.id}
												className={`feature-selector__item ${
													isSelected ? 'feature-selector__item--selected' : ''
												} ${disabled ? 'feature-selector__item--disabled' : ''}`}
											>
												<input
													type="checkbox"
													className="feature-selector__checkbox"
													checked={isSelected}
													onChange={() => handleToggle(feature.id)}
													disabled={disabled}
												/>

												<div className="feature-selector__item-content">
													<div className="feature-selector__item-title">
														{feature.name}
														{isSelected && (
															<Check size={14} className="feature-selector__check-icon" />
														)}
													</div>
													{feature.description && (
														<div className="feature-selector__item-description">
															{feature.description}
														</div>
													)}
												</div>
											</label>
										)
									})}
								</div>
							</div>
						)
					})}
			</div>

			{/* Error Message */}
			{error && <div className="feature-selector__validation-error">{error}</div>}
		</div>
	)
}

FeatureSelector.propTypes = {
	value: PropTypes.arrayOf(PropTypes.number),
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	error: PropTypes.string,
}

export default FeatureSelector

